import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import { apiString as API_URL, ERROR, SUCCESS, WARNING } from '../utils/constants';
import b64toBlob from 'b64-to-blob';
import axios from 'axios';

import { convertObjectToArray } from '../utils/common';
import {
    // EngagementDraft,
    ReasonForVisit,
} from '../Models/DocumentEngagements/EngagementDraft.model';
import { gotoNextMenu, setAssessmentSuccess, updateAssessmentMenu } from './assessment.slice';
import { AssessmentHistoryPreview } from '../Models/Assessment/AssessmentHistoryPreview.model';

import { MemberAssessmentHistoryDto } from '../Models/Member/MemberAssessmentHistoryDto.model';
import { assessmentMenuEnum, phoneOptions } from '../utils/assessments';
import { PrepareMenuClass } from '../Models/Shared/PrepareMenuClass.model';
import { formatDate } from '../utils/dateHelpers';
import { DocumentEngagement } from '../Models/DocumentEngagements/DocumentEngagement.model';
import { DocumentEngagementType } from '../Enum/DocumentEngagementTypeEnum';
import { PartTwoDownloadEnum } from '../Enum/PartTwoDownloadEnum';
import { getMemberBasicInfo } from './memberDetail.slice';
import { DiagnosticStatus } from '../Models/Assessment/DiagnosticStatus.model';
import { setIsLoading, setShowMessage } from './shared.slice';
import { NavigateFunction } from 'react-router-dom';
import { closeHeidiWidget, HeidiPatientInfo, initHeidiWidget, openHeidiWidget, setHeidiWidgetToken } from '../utils/heidiWidget';
import { MemberAssessmentStatuses } from '../Models/Assessment/MemberAssessmentStatuses.model';
import { MemberStatusEnum } from '../Enum/MemberStatusEnum';
import { normalizeSummaryNoteConfirmation, setSummaryNotesContacted } from '../utils/summaryNoteConfirmation';

let heidiApplyPollingTimer: ReturnType<typeof setTimeout> | null = null;

/** Normalizes workflow statuses onto the draft root so persistence and history stay in sync. */
const _buildDocumentEngagementPayload = (draft: DocumentEngagement): DocumentEngagement => {
    const workflowState = draft.DraftWorkflowState as any;

    return {
        ...(draft as DocumentEngagement),
        Type: DocumentEngagementType.Draft,
        SdohAssessmentStatus: draft.SdohAssessmentStatus ?? workflowState?.SdohAssessmentStatus,
        MiniMentalExamStatus: draft.MiniMentalExamStatus ?? workflowState?.MiniMentalExamStatus,
        SuicidalHomicidalIdeationStatus: draft.SuicidalHomicidalIdeationStatus ?? workflowState?.SuicidalHomicidalIdeationStatus,
        VitalSignStatus: draft.VitalSignStatus ?? workflowState?.VitalSignStatus,
        HamiltonAnxietyScaleStatus: draft.HamiltonAnxietyScaleStatus ?? workflowState?.HamiltonAnxietyScaleStatus,
        AbuseHistoryStatus: draft.AbuseHistoryStatus ?? workflowState?.AbuseHistoryStatus,
        LegalHistoryStatus: draft.LegalHistoryStatus ?? workflowState?.LegalHistoryStatus,
        CulturalConsiderationsStatus: draft.CulturalConsiderationsStatus ?? workflowState?.CulturalConsiderationsStatus,
        DevelopmentalHistoryStatus: draft.DevelopmentalHistoryStatus ?? workflowState?.DevelopmentalHistoryStatus,
        EatingDisorderStatus: draft.EatingDisorderStatus ?? workflowState?.EatingDisorderStatus,
        MentalHealthTreatmentHistoryStatus: draft.MentalHealthTreatmentHistoryStatus ?? workflowState?.MentalHealthTreatmentHistoryStatus,
        CurrentNeedsStatus: draft.CurrentNeedsStatus ?? workflowState?.CurrentNeedsStatus,
        GAFScaleStatus: draft.GAFScaleStatus ?? workflowState?.GAFScaleStatus,
        TobaccoAndDrugAssessmentStatus: draft.TobaccoAndDrugAssessmentStatus ?? workflowState?.TobaccoAndDrugAssessmentStatus,
        GoalStatus: draft.GoalStatus ?? workflowState?.GoalStatus,
        MedicationReconciliationStatus: draft.MedicationReconciliationStatus ?? workflowState?.MedicationReconciliationStatus,
        CarePlanStatus: draft.CarePlanStatus ?? workflowState?.CarePlanStatus,
        RequestStatus: draft.RequestStatus ?? workflowState?.RequestStatus,
        TransitionalCareStatus: draft.TransitionalCareStatus ?? workflowState?.TransitionalCareStatus,
        EducationStatus: draft.EducationStatus ?? workflowState?.EducationStatus,
        DocumentEngagementStatus: draft.DocumentEngagementStatus ?? workflowState?.DocumentEngagementStatus,
        FollowUpStatus: draft.FollowUpStatus ?? workflowState?.FollowUpStatus,
        InterventionStatus: draft.InterventionStatus ?? workflowState?.InterventionStatus,
        ProgressNoteStatus: draft.ProgressNoteStatus ?? workflowState?.ProgressNoteStatus,
        RemoteMonitoringStatus: draft.RemoteMonitoringStatus ?? workflowState?.RemoteMonitoringStatus,
        HealthPlanStatus: draft.HealthPlanStatus ?? workflowState?.HealthPlanStatus,
        DemographicStatus: draft.DemographicStatus ?? workflowState?.DemographicStatus,
        AllergyStatus: draft.AllergyStatus ?? workflowState?.AllergyStatus,
        CareCircleStatus: draft.CareCircleStatus ?? workflowState?.CareCircleStatus,
        TabletStatus: draft.TabletStatus ?? workflowState?.TabletStatus,
        PcpSpecialistStatus: draft.PcpSpecialistStatus ?? workflowState?.PcpSpecialistStatus,
        DraftWorkflowState: {
            ...workflowState,
            IsCompletedBasicInfo: draft.IsCompletedBasicInfo,
            IsCompletedAddressInfo: draft.IsCompletedAddressInfo,
            IsCompletedContactInfo: draft.IsCompletedContactInfo,
            DateOfOnboard: draft.DateOfOnboard,
            MemberStatus: draft.MemberStatus,
            CreatedOn: draft.CreatedOn,
            CreatedBy: draft.CreatedBy,
            IsCarePlanReviewed: draft.IsCarePlanReviewed,
            AddKeyContact: draft.AddKeyContact,
            EngageWithMember: draft.EngageWithMember,
            AddPlanOfCare: draft.AddPlanOfCare,
            MedicalReconcilation: draft.MedicalReconcilation,
            PlanDischargeLocation: draft.PlanDischargeLocation,
            IsInTransition: draft.IsInTransition,
            HraEducationFollowUpRequestIds: draft.HraEducationFollowUpRequestIds,
        },
    };
};

const getApiErrorMessage = (error: any, fallback: string): string => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) {
        return data;
    }

    if (data?.error && typeof data.error === 'string') {
        return data.error;
    }

    if (data?.ErrorMessage && typeof data.ErrorMessage === 'string') {
        return data.ErrorMessage;
    }

    if (data?.message && typeof data.message === 'string') {
        return data.message;
    }

    if (data?.title && typeof data.title === 'string') {
        return data.title;
    }

    if (error?.message && typeof error.message === 'string') {
        return error.message;
    }

    return fallback;
};

const formatHeidiAssessmentIssues = (assessmentIssues: any[]): string => {
    if (!assessmentIssues?.length) {
        return '';
    }

    const truncateIssueText = (text: string, maxLength = 80) => (text.length > maxLength ? `${text.slice(0, maxLength)}...` : text);
    const visibleIssues = assessmentIssues.slice(0, 3);
    const issueSummary = visibleIssues
        .map((x) => {
            if (x?.AssessmentName && x?.Message) {
                return `${x.AssessmentName}: ${truncateIssueText(x.Message)}`;
            }
            return x?.AssessmentName || (x?.Message ? truncateIssueText(x.Message) : '');
        })
        .filter(Boolean)
        .join('; ');
    const extraCount = assessmentIssues.length - visibleIssues.length;
    const extraSuffix = extraCount > 0 ? ` and ${extraCount} more` : '';

    return issueSummary ? `${issueSummary}${extraSuffix}` : '';
};

const clearHeidiApplyPollingTimer = () => {
    if (heidiApplyPollingTimer) {
        clearTimeout(heidiApplyPollingTimer);
        heidiApplyPollingTimer = null;
    }
};

const documentEngagementSlice = createSlice({
    name: 'documentEngagement',
    initialState: {
        meansOfEngagements: [],
        reasonForVisits: [],
        engagementDraft: new DocumentEngagement(),
        engagementDraftPreviews: [] as AssessmentHistoryPreview[],
        assessmentHistoryPreviews: [] as AssessmentHistoryPreview[],
        memberAssessmentHistory: new MemberAssessmentHistoryDto(),
        documentEngagements: [] as DocumentEngagement[],
        documentEngagement: new DocumentEngagement(),
        generatingPdfEngagementIds: [] as string[],
        appointmentId: '',
        showSummaryNoteModal: false,
        showDocEngagementModal: false,
        isFetchingDocumentEngagements: false,
        isFetchingPreviews: false,
        isFetchingHistory: false,
        isOpenSavedDraftsModel: false,
        isOpenSignConsentModel: false,
        isMemberHistoryFetching: false,
        isFetching: false,
        isSaving: false,
        isGeneratingPDF: false,
        downloadVerificationModal: { Show: false, ShowVerificationOptions: false, Message: '', EngagementId: '' },
        lastYearlyBHDiagnostic: new DiagnosticStatus(),
        isOpenAISessionId: false,
        heidiApplyJobId: '',
        heidiApplyStatus: 'Idle',
        heidiApplyTotalAssessments: 0,
        heidiApplyCompletedAssessments: 0,
        heidiApplyProgressPercentage: 0,
        heidiApplyLastCompletedAssessment: '',
    },
    reducers: {
        _setMeansOfEngagements(state, action) {
            state.meansOfEngagements = action.payload;
        },
        _setReasonForVisits(state, action) {
            state.reasonForVisits = action.payload;
        },
        _setEngagementDraft(state, action) {
            state.engagementDraft = normalizeSummaryNoteConfirmation(action.payload);
        },
        _setAppointmentId(state, action) {
            state.appointmentId = action.payload;
        },
        _setEngagementDraftPreviews(state, action) {
            state.engagementDraftPreviews = action.payload;
        },
        _setAssessmentHistoryPreviews(state, action) {
            state.assessmentHistoryPreviews = action.payload;
        },
        _setMemberAssessmentHistory(state, action) {
            state.memberAssessmentHistory = action.payload;
        },
        _setDocumentEngagements(state, action) {
            state.documentEngagements = action.payload;
        },
        _setDocumentEngagement(state, action) {
            state.documentEngagement = action.payload;
        },
        _toggleShowSummaryNoteModal(state, action) {
            state.showSummaryNoteModal = action.payload;
        },
        _toggleShowDocEngagementModal(state, action) {
            state.showDocEngagementModal = action.payload;
        },
        _toggleIsFetchingPreviews(state, action) {
            state.isFetchingPreviews = action.payload;
        },
        _toggleIsFetchingDocumentEngagements(state, action) {
            state.isFetchingDocumentEngagements = action.payload;
        },
        _toggleIsFetchingHistory(state, action) {
            state.isFetchingHistory = action.payload;
        },
        _toggleIsFetching(state, action) {
            state.isFetching = action.payload;
        },
        _toggleIsSaving(state, action) {
            state.isSaving = action.payload;
        },
        _toggleIsOpenSavedDraftsModel(state, action) {
            state.isOpenSavedDraftsModel = action.payload;
        },
        _toggleIsOpenSignConsentModel(state, action) {
            state.isOpenSignConsentModel = action.payload;
        },
        _toggleIsMemberHistoryFetching(state, action) {
            state.isMemberHistoryFetching = action.payload;
        },
        _toggleIsGeneratingPDF(state, action) {
            state.isGeneratingPDF = action.payload;
        },
        _toggleGeneratingPdfEngagementIds(state, action) {
            if (state.generatingPdfEngagementIds.some((id) => id === action.payload)) {
                state.generatingPdfEngagementIds = state.generatingPdfEngagementIds.filter((id) => id !== action.payload);
            } else {
                state.generatingPdfEngagementIds = [...state.generatingPdfEngagementIds, action.payload];
            }
        },
        _setDownloadVerificationModal(state, action) {
            state.downloadVerificationModal = action.payload;
        },
        _resetDocumentEngagement(state, action) {
            state.meansOfEngagements = [];
            state.reasonForVisits = [];
            state.engagementDraft = new DocumentEngagement();
            state.engagementDraftPreviews = [] as AssessmentHistoryPreview[];
            state.assessmentHistoryPreviews = [] as AssessmentHistoryPreview[];
            state.memberAssessmentHistory = new MemberAssessmentHistoryDto();
            state.documentEngagements = [] as DocumentEngagement[];
            state.documentEngagement = new DocumentEngagement();
            state.generatingPdfEngagementIds = [] as string[];
            state.appointmentId = '';
            state.showSummaryNoteModal = false;
            state.showDocEngagementModal = false;
            state.isFetchingDocumentEngagements = false;
            state.isFetchingPreviews = false;
            state.isFetchingHistory = false;
            state.isOpenSavedDraftsModel = false;
            state.isOpenSignConsentModel = false;
            state.isMemberHistoryFetching = false;
            state.isFetching = false;
            state.isSaving = false;
            state.isGeneratingPDF = false;
            state.downloadVerificationModal = { Show: false, ShowVerificationOptions: false, Message: '', EngagementId: '' };
            state.heidiApplyJobId = '';
            state.heidiApplyStatus = 'Idle';
            state.heidiApplyTotalAssessments = 0;
            state.heidiApplyCompletedAssessments = 0;
            state.heidiApplyProgressPercentage = 0;
            state.heidiApplyLastCompletedAssessment = '';
        },
        _setLastYearlyBHDiagnostic(state, action) {
            state.lastYearlyBHDiagnostic = action.payload;
        },
        _toggleIsOpenAISessionId(state, action) {
            state.isOpenAISessionId = action.payload;
        },
        _setHeidiApplyProgress(state, action) {
            const payload = action.payload || {};
            const normalizedStatus =
                payload.Status === 0
                    ? 'Queued'
                    : payload.Status === 1
                    ? 'InProgress'
                    : payload.Status === 2
                    ? 'Completed'
                    : payload.Status === 3
                    ? 'Failed'
                    : payload.Status;
            state.heidiApplyJobId = payload.JobId ?? state.heidiApplyJobId;
            state.heidiApplyStatus = normalizedStatus ?? state.heidiApplyStatus;
            state.heidiApplyTotalAssessments = payload.TotalAssessments ?? state.heidiApplyTotalAssessments;
            state.heidiApplyCompletedAssessments = payload.CompletedAssessments ?? state.heidiApplyCompletedAssessments;
            state.heidiApplyProgressPercentage = payload.Percentage ?? state.heidiApplyProgressPercentage;
            state.heidiApplyLastCompletedAssessment = payload.LastCompletedAssessment ?? state.heidiApplyLastCompletedAssessment;
        },
        _resetHeidiApplyProgress(state) {
            state.heidiApplyJobId = '';
            state.heidiApplyStatus = 'Idle';
            state.heidiApplyTotalAssessments = 0;
            state.heidiApplyCompletedAssessments = 0;
            state.heidiApplyProgressPercentage = 0;
            state.heidiApplyLastCompletedAssessment = '';
        },
    },
});

const {
    _setMeansOfEngagements,
    _setReasonForVisits,
    _setEngagementDraft,
    _setAppointmentId,
    _setEngagementDraftPreviews,
    _setAssessmentHistoryPreviews,
    _setMemberAssessmentHistory,
    _setDocumentEngagements,
    _setDocumentEngagement,
    _toggleGeneratingPdfEngagementIds,
    _toggleShowSummaryNoteModal,
    _toggleShowDocEngagementModal,
    _toggleIsFetchingPreviews,
    _toggleIsFetchingDocumentEngagements,
    _toggleIsFetchingHistory,
    _toggleIsFetching,
    _toggleIsSaving,
    _toggleIsOpenSavedDraftsModel,
    _toggleIsOpenSignConsentModel,
    _toggleIsMemberHistoryFetching,
    _toggleIsGeneratingPDF,
    _setDownloadVerificationModal,
    _resetDocumentEngagement,
    _setLastYearlyBHDiagnostic,
    _toggleIsOpenAISessionId,
    _setHeidiApplyProgress,
    _resetHeidiApplyProgress,
} = documentEngagementSlice.actions;

export const resetDocumentEngagement = (): AppThunk => (dispatch, getState) => {
    clearHeidiApplyPollingTimer();
    dispatch(_resetDocumentEngagement(''));
    dispatch(_resetHeidiApplyProgress());
};

export const toggleOpenSignConsentModel =
    (show: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_toggleIsOpenSignConsentModel(show));
    };

export const handleCloseSignConsentModal = (): AppThunk => (dispatch) => {
    dispatch(_toggleIsOpenSignConsentModel(false));
};

export const setDocumentEngagements =
    (state: DocumentEngagement[]): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setDocumentEngagements(state));
    };

export const setMemberAssessmentHistory =
    (state: MemberAssessmentHistoryDto): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setMemberAssessmentHistory(state));
    };

export const fetchLastYearlyBHDiagnostic =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/documentengagement/getlastyearlybhdiagnostic`, { params: { memberId } });
        dispatch(_setLastYearlyBHDiagnostic(response.data));
    };

export const getDocumentEngagementByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetchingDocumentEngagements(true));
        const response = await axios.get(`${API_URL}/documentengagement/getdocumentengagementbymemberid`, { params: { memberId } });
        dispatch(_setDocumentEngagements(response.data));
        dispatch(_toggleIsFetchingDocumentEngagements(false));
    };

export const getEngagementDraftPreview =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetchingPreviews(true));
        const response = await axios.get(`${API_URL}/assessment/getengagementdraftpreview`, { params: { memberId } });
        dispatch(_setEngagementDraftPreviews(response.data));
        dispatch(_toggleIsFetchingPreviews(false));
    };

export const getAssessmentWorkflowHistory =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetchingHistory(true));
        const response = await axios.get(`${API_URL}/assessment/getassessmentworkflowhistory`, { params: { memberId } });
        dispatch(_setAssessmentHistoryPreviews(response.data));
        dispatch(_toggleIsFetchingHistory(false));
    };

export const fetchMemberAssessmentHistory =
    (memberId: string, id: string = null, role: string = null): AppThunk =>
    async (dispatch, getState) => {
        if (Boolean(role || id)) {
            const response = await axios.get(`${API_URL}/assessment/getmemberassessmenthistoryforworkflow`, {
                params: { memberId, id, role },
            });
            dispatch(_setMemberAssessmentHistory(response.data));
        }
    };

export const getMemberAssessmentHistoryForWorkflow =
    (memberId: string, id: string = null, role: string = null): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsMemberHistoryFetching(true));
        if (Boolean(role || id)) {
            const response = await axios.get(`${API_URL}/assessment/getmemberassessmenthistoryforworkflow`, {
                params: { memberId, id, role },
            });
            dispatch(_setMemberAssessmentHistory(response.data));
            const engagementDraft = response.data?.EngagementDraft as DocumentEngagement | undefined;
            if (!engagementDraft) {
                dispatch(_toggleIsMemberHistoryFetching(false));
                dispatch(setShowMessage(true, 'Unable to load assessment draft for this workflow.', ERROR));
                return;
            }
            if (engagementDraft.Id === null) {
                dispatch(saveEngagementDraft(response.data, false));
            }
            const currentMenu = engagementDraft.CurrentMenu || assessmentMenuEnum.CONSENT;
            role = engagementDraft.AssessedRole?.RoleName || role;
            dispatch(_setEngagementDraft(engagementDraft));

            dispatch(
                updateAssessmentMenu({
                    ActiveMenu: currentMenu,
                    Role: role,
                    EngagementDraft: engagementDraft,
                    MemberAssessmentHistory: response.data,
                } as PrepareMenuClass)
            );
            dispatch(_toggleIsMemberHistoryFetching(false));
            dispatch(toggleIsOpenSavedDraftsModel(false));

            if (Boolean(engagementDraft.ConsentFormId)) {
                dispatch(
                    updateAssessmentMenu({
                        ActiveMenu: currentMenu,
                        EngagementDraft: engagementDraft,
                        MemberAssessmentHistory: response.data,
                    } as PrepareMenuClass)
                );
            }
        }
    };

export const createNewMemberAssessment =
    (memberId: string, role: string, diagnosticType: number, onSuccess: (id: string) => void): AppThunk =>
    async (dispatch, getState) => {
        try {
            dispatch(setIsLoading(true));
            const response = await axios.get(`${API_URL}/assessment/createnewmemberassessment`, {
                params: { memberId, role, diagnosticType },
            });
            dispatch(_setMemberAssessmentHistory(response.data));
            const engagementDraft = response.data?.EngagementDraft as DocumentEngagement | undefined;
            if (!engagementDraft) {
                dispatch(setShowMessage(true, 'An error occurred while creating a new member assessment.', ERROR));
                return;
            }
            const currentMenu = engagementDraft.CurrentMenu || assessmentMenuEnum.CONSENT;
            const resolvedRole = engagementDraft.AssessedRole?.RoleName || role;
            dispatch(_setEngagementDraft(engagementDraft));
            dispatch(
                updateAssessmentMenu({
                    ActiveMenu: currentMenu,
                    Role: resolvedRole,
                    EngagementDraft: engagementDraft,
                    MemberAssessmentHistory: response.data,
                } as PrepareMenuClass)
            );
            onSuccess(engagementDraft.Id);
        } catch (error: any) {
            console.error(error);
            dispatch(setShowMessage(true, 'An error occurred while creating a new member assessment.', ERROR));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const deleteAssessmentDrafts =
    (ids: string[]): AppThunk =>
    async (dispatch, getState) => {
        const engagementDraftPreviews = getState().documentEngagement.engagementDraftPreviews;
        dispatch(_setEngagementDraftPreviews(engagementDraftPreviews.filter((e) => !ids.some((i) => i === e.Id))));
        const parseIds = ids.toString();
        await axios.delete(`${API_URL}/assessment/deleteassessmentdrafts`, { params: { parseIds } });
        dispatch(setAssessmentSuccess({ Show: true, Message: 'Deleted Successfully!' }));
    };

export const getMeansOfEngagements = (): AppThunk => async (dispatch, getState) => {
    dispatch(_toggleIsFetching(true));
    const response = await axios.get(`${API_URL}/documentengagement/getmeansofengagement`);
    dispatch(_setMeansOfEngagements(convertObjectToArray(response.data)));
    dispatch(_toggleIsFetching(false));
};

export const getReasonForVisit =
    (callback?: Function): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsFetching(true));
        const response = await axios.get(`${API_URL}/documentengagement/getreasonforvisit`);
        response.data = response.data.filter(
            (r) =>
                r.Id !== '56695e83ad78ee0e48b04a69' &&
                r.Id !== '56695e83ad78ee0e48b04a62' &&
                r.Id !== '56695e83ad78ee0e48b04a67' &&
                r.Id !== '56695e83ad78ee0e48b04a68'
        );
        dispatch(_setReasonForVisits(response.data));
        dispatch(_toggleIsFetching(false));
        if (Boolean(callback)) {
            callback(response.data);
        }
    };

export const setReasonForVisit =
    (list: ReasonForVisit[]): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setReasonForVisits(list));
    };

export const toggleShowSummaryNoteModal =
    (show: boolean): AppThunk =>
    (dispatch, getState) => {
        dispatch(_toggleShowSummaryNoteModal(show));
    };

export const toggleShowDocEngagementModal =
    (show: boolean, documentEngagement = new DocumentEngagement()): AppThunk =>
    (dispatch, getState) => {
        dispatch(_toggleShowDocEngagementModal(show));
        dispatch(_setDocumentEngagement(documentEngagement));
    };

export const applyAISession =
    (memberId: string, aiSessionId: string, selectedRole: string, navigate: NavigateFunction, diagnosticType: number = 0): AppThunk =>
    async (dispatch) => {
        clearHeidiApplyPollingTimer();
        dispatch(_resetHeidiApplyProgress());
        dispatch(
            _setHeidiApplyProgress({
                Status: 'Queued',
            })
        );
        try {
            const startResponse = await axios.post(`${API_URL}/documentengagement/applyheidisession/start`, {
                memberId,
                aiSessionId,
                selectedRole,
                diagnosticType,
            });
            const jobId = startResponse.data?.JobId;
            if (!jobId) {
                throw new Error('Heidi session job could not be started.');
            }

            dispatch(
                _setHeidiApplyProgress({
                    JobId: jobId,
                    Status: 'Queued',
                    TotalAssessments: 0,
                    CompletedAssessments: 0,
                    Percentage: 0,
                    LastCompletedAssessment: '',
                })
            );

            dispatch(setShowMessage(true, 'Heidi session started. Progress will update automatically.', SUCCESS));

            const pollStatus = async () => {
                try {
                    const statusResponse = await axios.get(`${API_URL}/documentengagement/applyheidisession/status/${jobId}`);
                    const status = statusResponse.data || {};
                    dispatch(_setHeidiApplyProgress(status));

                    const statusValue = status?.Status === 2 ? 'Completed' : status?.Status === 3 ? 'Failed' : status?.Status;
                    const isCompleted = statusValue === 'Completed';
                    const isFailed = statusValue === 'Failed';

                    if (isCompleted) {
                        clearHeidiApplyPollingTimer();
                        const assessmentIssues = status?.AssessmentIssues ?? [];
                        if (assessmentIssues.length > 0) {
                            const issueSummary = formatHeidiAssessmentIssues(assessmentIssues);
                            dispatch(
                                setShowMessage(
                                    true,
                                    issueSummary
                                        ? `Heidi session processed with issues: ${issueSummary}`
                                        : 'Heidi session processed with issues in one or more assessments.',
                                    WARNING
                                )
                            );
                        } else {
                            dispatch(setShowMessage(true, 'Heidi session processing completed.', SUCCESS));
                        }

                        const engagementDraftId = status?.EngagementDraftId;
                        dispatch(_resetHeidiApplyProgress());
                        if (engagementDraftId) {
                            navigate(`assessment/${engagementDraftId}`);
                        }

                        return;
                    }

                    if (isFailed) {
                        clearHeidiApplyPollingTimer();
                        const errorMessage = status?.ErrorMessage || 'Heidi session processing failed.';
                        const issueSummary = formatHeidiAssessmentIssues(status?.AssessmentIssues ?? []);
                        dispatch(setShowMessage(true, issueSummary ? `${errorMessage} Issues: ${issueSummary}` : errorMessage, ERROR));
                        dispatch(_resetHeidiApplyProgress());
                        return;
                    }

                    heidiApplyPollingTimer = setTimeout(pollStatus, 10000);
                } catch (error: any) {
                    clearHeidiApplyPollingTimer();
                    dispatch(setShowMessage(true, getApiErrorMessage(error, 'Unable to retrieve Heidi session progress.'), ERROR));
                    dispatch(_resetHeidiApplyProgress());
                }
            };

            await pollStatus();
        } catch (error: any) {
            clearHeidiApplyPollingTimer();
            dispatch(setShowMessage(true, getApiErrorMessage(error, 'Heidi session could not be started.'), ERROR));
            dispatch(_resetHeidiApplyProgress());
        }
    };

const fetchHeidiWidgetToken = async (): Promise<string> => {
    const response = await axios.get(`${API_URL}/documentengagement/heidiwidgettoken`);
    const token = response.data?.Token;
    if (!token) {
        throw new Error('Unable to obtain a Heidi widget token.');
    }
    return token;
};

export interface HeidiContextNoteItem {
    id: string;
    date: string;
    preview: string;
    providerName?: string;
    fullText: string;
}

export interface HeidiContextNotesResult {
    source: string;
    notes: HeidiContextNoteItem[];
}

const mapHeidiContextNote = (n: any): HeidiContextNoteItem => ({
    id: n?.Id ?? n?.id ?? '',
    date: n?.Date ?? n?.date ?? '',
    preview: n?.Preview ?? n?.preview ?? '',
    providerName: n?.ProviderName ?? n?.providerName ?? '',
    fullText: n?.FullText ?? n?.fullText ?? '',
});

/** Prior note options for Heidi Assessment 2.0 context selection (Heidi sessions or Progress Notes fallback). */
export const fetchHeidiContextNotes = async (memberId: string): Promise<HeidiContextNotesResult> => {
    try {
        const response = await axios.get(`${API_URL}/documentengagement/heidicontextnotes/${memberId}`);
        const data = response.data;
        const notes = (data?.Notes ?? data?.notes ?? []).map(mapHeidiContextNote).filter((n: HeidiContextNoteItem) => n.id || n.fullText);
        return {
            source: data?.Source ?? data?.source ?? '',
            notes,
        };
    } catch {
        return { source: '', notes: [] };
    }
};

export const fetchMemberJourneySummaryForContext = async (memberId: string): Promise<string> => {
    try {
        const response = await axios.get(`${API_URL}/MemberLifecycle/${memberId}`);
        const summary = response.data?.JourneySummary ?? response.data?.journeySummary;
        return typeof summary === 'string' ? summary.trim() : '';
    } catch {
        return '';
    }
};

export const fetchMemberAiSummaryForContext = async (memberId: string): Promise<string> => {
    try {
        const response = await axios.get(`${API_URL}/membersummary/cached/${memberId}`);
        if (response.status !== 200 || !response.data) {
            return '';
        }
        const markdown = response.data.Markdown ?? response.data.markdown ?? '';
        return typeof markdown === 'string' ? markdown.trim() : '';
    } catch {
        return '';
    }
};

/** Best-effort: tag Heidi session with CHP member id so later context-note lookups can filter by member. */
const linkHeidiSessionToMember = async (memberId: string, sessionId: string): Promise<void> => {
    if (!memberId || !sessionId) {
        return;
    }
    try {
        await axios.post(`${API_URL}/documentengagement/heidisessionpatient`, { memberId, sessionId });
    } catch {
        // Non-blocking — Progress Notes fallback still works if tagging fails.
    }
};

export const launchHeidiWidget =
    (
        memberId: string,
        selectedRole: string,
        patient: HeidiPatientInfo,
        navigate: NavigateFunction,
        context?: string,
        diagnosticType: number = 0
    ): AppThunk<Promise<void>> =>
    async (dispatch) => {
        // The widget stays open across route changes, so the note can be pushed from any page.
        // applyAISession navigates relative to the assessment dashboard; force an absolute path here.
        const navigateFromDashboard = ((to: any, options?: any) =>
            typeof to === 'string' && !to.startsWith('/')
                ? navigate(`/provider/members-list/member-chart/${memberId}/assessment-dashboard/${to}`, options)
                : navigate(to, options)) as NavigateFunction;
        const contextText = typeof context === 'string' ? context.trim() : '';
        try {
            const token = await fetchHeidiWidgetToken();
            let lastSessionId = '';
            await initHeidiWidget(token, {
                onSessionStarted: (sessionId) => {
                    lastSessionId = sessionId;
                    void linkHeidiSessionToMember(memberId, sessionId);
                },
                onPushData: (data) => {
                    const sessionId = data?.sessionId || lastSessionId;
                    if (!sessionId) {
                        dispatch(setShowMessage(true, 'Heidi did not provide a session ID for this note.', ERROR));
                        return;
                    }
                    // Ensure member link even if onSessionStarted was missed.
                    void linkHeidiSessionToMember(memberId, sessionId);
                    closeHeidiWidget();
                    dispatch(applyAISession(memberId, sessionId, selectedRole, navigateFromDashboard, diagnosticType));
                },
                onTokenExpired: async () => {
                    try {
                        setHeidiWidgetToken(await fetchHeidiWidgetToken());
                    } catch (error: any) {
                        dispatch(setShowMessage(true, getApiErrorMessage(error, 'Unable to refresh the Heidi widget token.'), ERROR));
                    }
                },
            });
            await openHeidiWidget({ patient, startNewSession: true, context: contextText || undefined });
        } catch (error: any) {
            dispatch(setShowMessage(true, getApiErrorMessage(error, 'Unable to launch the Heidi widget.'), ERROR));
        }
    };

export const updateMemberAssessmentStatuses =
    (memberAssessmentHistory: MemberAssessmentHistoryDto): AppThunk<Promise<void>> =>
    async (dispatch) => {
        dispatch(setMemberAssessmentHistory(memberAssessmentHistory));
        try {
            await axios.put(`${API_URL}/assessment/updatememberassessmentstatuses`, memberAssessmentHistory.MemberAssessmentStatuses);
        } catch (error: any) {
            const message = getApiErrorMessage(error, 'Unable to update member assessment statuses.');
            dispatch(setShowMessage(true, message, ERROR));
        }
    };

export const saveEngagementDraft =
    (memberAssessmentHistoryDto: MemberAssessmentHistoryDto, gotoNext: boolean = false, callBack?: Function): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        const chartDetails = getState().memberDemographicsDetails.chartDetails;
        const engagementDraft = normalizeSummaryNoteConfirmation(memberAssessmentHistoryDto.EngagementDraft);
        let isSaved = false;
        try {
            const payload = {
                ...engagementDraft,
                Type: engagementDraft?.Type || DocumentEngagementType.Draft,
            };
            if (chartDetails?.Status !== engagementDraft?.MemberStatus) {
                await axios.put(`${API_URL}/documentengagement/updatememberstatusfordraft`, engagementDraft);
            }
            const url = Boolean(payload.Id) ? `${API_URL}/documentengagement/updatedraft` : `${API_URL}/documentengagement/insertdraft`;
            const response = await axios.put(url, payload);
            // debugger;
            const updatedEngagementDraft = response.data as DocumentEngagement;
            // const updatedEngagementDraft = payload.Id ? payload : (response.data as DocumentEngagement);
            dispatch(_setEngagementDraft(updatedEngagementDraft));
            const memberAssessmentHistory = memberAssessmentHistoryDto;
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                EngagementDraft: updatedEngagementDraft,
                MemberAssessmentStatuses: {
                    ...(memberAssessmentHistory.MemberAssessmentStatuses || ({} as MemberAssessmentStatuses)),
                    DocumentEngagementId: updatedEngagementDraft.Id,
                },
            };
            await dispatch(updateMemberAssessmentStatuses(updatedMemberAssessmentHistory));
            isSaved = true;
        } catch (error: any) {
            const message = getApiErrorMessage(error, 'Unable to save engagement draft.');
            dispatch(setShowMessage(true, message, ERROR));
        } finally {
            dispatch(_toggleIsSaving(false));
        }

        if (isSaved && gotoNext) {
            dispatch(gotoNextMenu());
        }
        if (isSaved && Boolean(callBack)) {
            callBack();
        }
        if (isSaved && !Boolean(engagementDraft.Id) && Boolean(engagementDraft.MemberId)) {
            dispatch(getMemberBasicInfo(engagementDraft.MemberId));
        }

        return isSaved;
    };

export const saveDocumentEngagement =
    (documentEngagement: DocumentEngagement, callBack?: Function): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));

        const request = Boolean(documentEngagement.Id)
            ? axios.put(`${API_URL}/documentengagement/updatedocumentengagement`, documentEngagement)
            : axios.post(`${API_URL}/documentengagement/insertdocumentengagement`, documentEngagement);
        request
            .then((response) => {
                dispatch(toggleShowDocEngagementModal(false));
                if (Boolean(callBack)) {
                    callBack();
                }
                dispatch(getDocumentEngagementByMemberId(documentEngagement.MemberId));
            })
            .finally(() => {
                dispatch(_toggleIsSaving(false));
            });
    };

export const finalizeEngagement =
    (engagementDraftId: string, notToEncounter: boolean, callBack?: Function): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        try {
            await axios.put(`${API_URL}/documentengagement/${engagementDraftId}/finalize`, null, {
                params: { notToEncounter },
            });
            if (Boolean(callBack)) {
                callBack();
            }
        } catch (error: any) {
            const message = getApiErrorMessage(error, 'Unable to finalize engagement.');
            dispatch(setShowMessage(true, message, ERROR));
        }
        dispatch(_toggleIsSaving(false));
    };

export const reopenEngagement =
    (engagementId: string, navigate: NavigateFunction, destination: string, callBack?: Function): AppThunk =>
    async (dispatch, getState) => {
        dispatch(_toggleIsSaving(true));
        try {
            const response = await axios.put(`${API_URL}/documentengagement/${engagementId}/reopen`);
            dispatch(_setEngagementDraft(response.data));
            const state = getState().documentEngagement;
            dispatch(_setDocumentEngagements(state.documentEngagements.filter((engagement) => engagement.Id !== engagementId)));
            dispatch(
                _setEngagementDraftPreviews([
                    ...state.engagementDraftPreviews.filter((draft) => draft.Id !== engagementId),
                    {
                        Id: response.data.Id,
                        CreatedDate: response.data.CreationTime,
                        Role: response.data.AssessedRole?.RoleName,
                        DiagnosticType: response.data.DiagnosticStatus?.DiagnosticType ?? 0,
                        IsSelected: false,
                        IsAiCreated: false,
                    },
                ])
            );
            navigate(destination);
            if (Boolean(callBack)) {
                callBack();
            }
        } catch (error: any) {
            const message = getApiErrorMessage(error, 'Unable to reopen engagement.');
            dispatch(setShowMessage(true, message, ERROR));
        }
        dispatch(_toggleIsSaving(false));
    };

export const setEngagementDraftPreviews =
    (engagementDraftPreviews: AssessmentHistoryPreview[]): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setEngagementDraftPreviews(engagementDraftPreviews));
    };

export const setEngagementDraft =
    (engagementDraft: DocumentEngagement): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setEngagementDraft(engagementDraft));
    };

export const setEngagementContacted =
    (contacted: boolean, engagementDraft: DocumentEngagement): AppThunk =>
    (dispatch) => {
        dispatch(
            setEngagementDraft({
                ...engagementDraft,
                Contacted: contacted,
                ContactResult: contacted ? phoneOptions[3].Name : engagementDraft.ContactResult,
                SummaryNotes: setSummaryNotesContacted(engagementDraft.SummaryNotes || [], contacted),
            })
        );
    };

export const setAppointmentId =
    (id: string): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setAppointmentId(id));
    };

export const updateAppointmentIdListToStatus =
    (appointmentStatus: number, callBack?: Function): AppThunk =>
    async (dispatch, getState) => {
        const appointmentId = getState().documentEngagement.appointmentId || getState().documentEngagement?.engagementDraft?.AssociatedAppointmentId;
        dispatch(_toggleIsSaving(true));
        axios
            .post(`${API_URL}/scheduling/UpdateAppointmentIdListToStatus/${appointmentStatus}`, [appointmentId])
            .then((response) => {
                dispatch(setAssessmentSuccess({ Show: true, Message: 'Appointment statuses successfully updated!' }));
            })
            .finally(() => {
                dispatch(_toggleIsSaving(false));
                if (callBack) {
                    callBack();
                }
            });
    };

export const toggleIsOpenSavedDraftsModel =
    (show: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_toggleIsOpenSavedDraftsModel(show));
    };

export const toggleIsOpenAISessionId =
    (show: boolean): AppThunk =>
    (dispatch) => {
        dispatch(_toggleIsOpenAISessionId(show));
    };

export const generateAssessmentWorkflowPDF =
    (engagementId: string, partTwoDownload = PartTwoDownloadEnum.None): AppThunk =>
    async (dispatch, getState) => {
        const chartDetails = getState().memberDemographicsDetails.chartDetails;
        dispatch(_toggleIsGeneratingPDF(true));
        dispatch(_toggleGeneratingPdfEngagementIds(engagementId));
        axios
            .get(`${API_URL}/assessment/generateAssessmentWorkflowPDF`, { params: { engagementId, partTwoDownload } })
            .then((response) => {
                var blob = b64toBlob(response.data.base64String, 'application/pdf');
                var blobUrl = URL.createObjectURL(blob);
                var a = document.createElement('a');
                var randamNumber = Math.floor(Math.random() * (1000 - 1 + 1)) + 1;
                var fileName = `${chartDetails?.LastName},${chartDetails?.FirstName}-Engagement-History-${formatDate(
                    new Date(),
                    'MM-dd-yyyy'
                )}-${randamNumber}`;
                document.body.appendChild(a);
                a.style.display = 'none';
                a.href = blobUrl;
                a.download = fileName + '.pdf';
                a.click();
                window.URL.revokeObjectURL(blobUrl);
                dispatch(resetDownloadVerificationModal());
            })
            .catch((err) => {
                dispatch(
                    _setDownloadVerificationModal({
                        Show: err.response?.data?.RequiredVerification,
                        Message: err.response?.data?.Message,
                        EngagementId: engagementId,
                    })
                );
            })
            .finally(() => {
                dispatch(_toggleGeneratingPdfEngagementIds(engagementId));
                dispatch(_toggleIsGeneratingPDF(false));
            });
    };

export const resetDownloadVerificationModal =
    (data?: any): AppThunk =>
    (dispatch, getState) => {
        dispatch(_setDownloadVerificationModal(data || { Show: false, ShowVerificationOptions: false, Message: '', EngagementId: '' }));
    };

export const checkIsOnboardedAndGetReason =
    (memberId: string, draftId?: string, callBack?: Function): AppThunk =>
    async (dispatch, getState) => {
        const response = await axios.get(`${API_URL}/assessment/ismemberonboarded`, { params: { memberId, draftId } });
        dispatch(getReasonForVisit((reasonForVisits) => callBack(reasonForVisits, response.data)));
    };

export default documentEngagementSlice.reducer;
